import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  calculateGratuity,
  confirmProbation,
  createSalaryRevision,
  executeTransfer,
  getEmployee,
  getEmployeeOnboarding,
  grantEmployeeAccess,
  listAuditLogs,
  listDepartments,
  listEmployees,
  listIamUsers,
  listPromotions,
  listSalaryRevisions,
  listTransfers,
  listDocumentTemplates,
  searchDocumentVault,
  generateDocument,
  getDocumentDownloadUrl,
  requestPromotion,
  reviewPromotion,
  updateIamUserRoles,
  updateOnboardingTask,
  type AuditEntry,
  type CareerPromotion,
  type Department,
  type EmployeeRecord,
  type EmployeeTransfer,
  type IamUser,
  type OnboardingTask,
  type OnboardingWorkflow,
  type SalaryRevision,
  type DocumentTemplate,
  type GeneratedDocument,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { Redacted } from '../components/Redacted';
import { StatusBadge } from '../components/StatusBadge';

const rupees = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="field-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function EmployeeDetail() {
  const { id = '' } = useParams();
  const { can, session } = useAuth();
  const [tab, setTab] = useState<'overview' | 'access' | 'onboarding' | 'promotions' | 'transfers' | 'revisions' | 'gratuity' | 'documents' | 'history'>('overview');
  const [employee, setEmployee] = useState<EmployeeRecord | null>(null);
  const [linkedUser, setLinkedUser] = useState<IamUser | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>(['employee']);
  const [savingAccess, setSavingAccess] = useState(false);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [onboardingWf, setOnboardingWf] = useState<OnboardingWorkflow | null>(null);
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);
  const [promotions, setPromotions] = useState<CareerPromotion[]>([]);
  const [transfers, setTransfers] = useState<EmployeeTransfer[]>([]);
  const [revisions, setRevisions] = useState<SalaryRevision[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [allEmployees, setAllEmployees] = useState<EmployeeRecord[]>([]);
  const [employeeDocs, setEmployeeDocs] = useState<GeneratedDocument[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [docModal, setDocModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [docTitleOverride, setDocTitleOverride] = useState('');
  const [previewDoc, setPreviewDoc] = useState<GeneratedDocument | null>(null);
  const [gratuityEstimate, setGratuityEstimate] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Modals
  const [probationModal, setProbationModal] = useState(false);
  const [probationStatus, setProbationStatus] = useState<'confirmed' | 'extended' | 'terminated'>('confirmed');
  const [probationNotes, setProbationNotes] = useState('Satisfactory completion of initial probation goals.');
  const [extendMonths, setExtendMonths] = useState(3);

  const [promoModal, setPromoModal] = useState(false);
  const [newDesignation, setNewDesignation] = useState('');
  const [newGrade, setNewGrade] = useState('Senior Grade L4');
  const [newCtc, setNewCtc] = useState(0);
  const [promoEffectiveDate, setPromoEffectiveDate] = useState('2026-09-01');
  const [promoJustification, setPromoJustification] = useState('Consistent exceptional performance, leadership in major project deliverables.');

  const [transferModal, setTransferModal] = useState(false);
  const [transferDeptId, setTransferDeptId] = useState('');
  const [transferLocation, setTransferLocation] = useState('Bangalore HQ');
  const [transferManagerId, setTransferManagerId] = useState('');
  const [transferEffectiveDate, setTransferEffectiveDate] = useState('2026-09-01');
  const [transferReason, setTransferReason] = useState('Strategic reallocation to core infrastructure team.');

  const [revisionModal, setRevisionModal] = useState(false);
  const [revisionType, setRevisionType] = useState<'appraisal' | 'market_correction' | 'promotion' | 'retention'>('appraisal');
  const [revisedCtc, setRevisedCtc] = useState(0);
  const [revisionEffectiveDate, setRevisionEffectiveDate] = useState('2026-09-01');
  const [revisionNotes, setRevisionNotes] = useState('Annual performance review merit increase.');

  async function loadData() {
    try {
      const emp = await getEmployee(id);
      setEmployee(emp);
      if (emp.compensation) {
        setNewCtc(emp.compensation.ctc_annual ? Math.round(emp.compensation.ctc_annual * 1.15) : 1500000);
        setRevisedCtc(emp.compensation.ctc_annual ? Math.round(emp.compensation.ctc_annual * 1.1) : 1500000);
      }

      const [onbRes, promoRes, transRes, revRes, deptsRes, empsRes, docsRes, templatesRes, iamUsersRes] = await Promise.all([
        getEmployeeOnboarding(id).catch(() => ({ data: null })),
        listPromotions(id).catch(() => ({ data: [] })),
        listTransfers(id).catch(() => ({ data: [] })),
        listSalaryRevisions(id).catch(() => ({ data: [] })),
        listDepartments().catch(() => ({ items: [] })),
        listEmployees().catch(() => ({ items: [] })),
        searchDocumentVault({ employeeId: id }).catch(() => ({ data: [] })),
        listDocumentTemplates().catch(() => ({ data: [] })),
        listIamUsers().catch(() => []),
      ]);

      const foundUser = (iamUsersRes as IamUser[]).find(
        (u) => u.employeeId === id || (emp.work_email && u.email.toLowerCase() === emp.work_email.toLowerCase())
      ) || null;
      setLinkedUser(foundUser);
      if (foundUser && foundUser.roleKeys?.length) {
        setUserRoles(foundUser.roleKeys);
      }

      if (onbRes.data) {
        setOnboardingWf(onbRes.data.workflow);
        setOnboardingTasks(onbRes.data.tasks || []);
      }
      setPromotions(promoRes.data || []);
      setTransfers(transRes.data || []);
      setRevisions(revRes.data || []);
      setDepartments(deptsRes.items || []);
      setAllEmployees(empsRes.items || []);
      setEmployeeDocs(docsRes.data || []);
      setTemplates(templatesRes.data || []);

      if (templatesRes.data?.length && !selectedTemplateId) {
        setSelectedTemplateId(templatesRes.data[0].id);
      }

      if (deptsRes.items?.length && !transferDeptId) {
        setTransferDeptId(deptsRes.items[0].id);
      }

      if (can('audit.view')) {
        listAuditLogs(id).then((r) => setHistory(r.items)).catch(() => undefined);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load employee record');
    }
  }

  async function handleSaveAccessRoles() {
    if (!employee) return;
    if (userRoles.length === 0) {
      setMsg({ type: 'error', text: 'At least one role must be selected' });
      return;
    }
    setSavingAccess(true);
    try {
      if (linkedUser) {
        await updateIamUserRoles(linkedUser.id, userRoles, `Role assignment updated from Employee Profile for ${employee.first_name} ${employee.last_name}`);
        setMsg({ type: 'success', text: `Persona roles updated successfully for ${employee.first_name} ${employee.last_name}!` });
      } else {
        await grantEmployeeAccess(employee.id, userRoles);
        setMsg({ type: 'success', text: `Login account created & persona roles granted for ${employee.first_name} ${employee.last_name}!` });
      }
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update access roles' });
    } finally {
      setSavingAccess(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, [id, can]);

  async function handleGenerateQuickDoc(templateIdToUse?: string) {
    if (!employee) return;
    const targetTplId = templateIdToUse || selectedTemplateId;
    if (!targetTplId) {
      setMsg({ type: 'error', text: 'Please select a document template.' });
      return;
    }

    try {
      const res = await generateDocument({
        templateId: targetTplId,
        employeeId: employee.id,
        title: docTitleOverride || undefined,
      });

      setMsg({ type: 'success', text: `Document ${res.data.document_number} generated successfully with status "${res.data.status}"!` });
      setDocModal(false);
      setDocTitleOverride('');
      await loadData();
      setTab('documents');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to generate document' });
    }
  }

  async function handleDownloadEmployeeDoc(docId: string) {
    try {
      const res = await getDocumentDownloadUrl(docId);
      window.open(res.downloadUrl, '_blank');
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Download link failed' });
    }
  }

  async function handleToggleTask(taskId: string, currentStatus: string) {
    try {
      const nextStatus = currentStatus === 'completed' ? 'in_progress' : 'completed';
      await updateOnboardingTask(taskId, { status: nextStatus });
      setMsg({ type: 'success', text: `Task marked as ${nextStatus.toUpperCase()}` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update task' });
    }
  }

  async function handleConfirmProbation(e: React.FormEvent) {
    e.preventDefault();
    if (!onboardingWf) return;
    try {
      await confirmProbation(onboardingWf.id, {
        confirmationStatus: probationStatus,
        notes: probationNotes,
        extendMonths: probationStatus === 'extended' ? extendMonths : undefined,
      });
      setMsg({ type: 'success', text: `Probation status updated to: ${probationStatus.toUpperCase()}` });
      setProbationModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to confirm probation' });
    }
  }

  async function handleRequestPromotion(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    try {
      await requestPromotion({
        employeeId: employee.id,
        newDesignation,
        newGrade,
        newCtc: Number(newCtc),
        effectiveDate: promoEffectiveDate,
        justification: promoJustification,
      });
      setMsg({ type: 'success', text: `Promotion request submitted for ${newDesignation}!` });
      setPromoModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit promotion request' });
    }
  }

  async function handleReviewPromotion(promoId: string, status: 'approved' | 'rejected') {
    try {
      await reviewPromotion(promoId, {
        status,
        remarks: status === 'approved' ? 'Approved by leadership panel.' : 'Pending further performance review.',
      });
      setMsg({ type: 'success', text: `Promotion ${status.toUpperCase()}!` });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to review promotion' });
    }
  }

  async function handleExecuteTransfer(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    try {
      await executeTransfer({
        employeeId: employee.id,
        transferType: 'department_location',
        toDepartmentId: transferDeptId || null,
        toLocation: transferLocation || null,
        toManagerId: transferManagerId || null,
        effectiveDate: transferEffectiveDate,
        reason: transferReason,
      });
      setMsg({ type: 'success', text: 'Transfer executed and organizational placement updated!' });
      setTransferModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to execute transfer' });
    }
  }

  async function handleCreateRevision(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    try {
      await createSalaryRevision({
        employeeId: employee.id,
        revisionType,
        newCtcAnnual: Number(revisedCtc),
        effectiveFrom: revisionEffectiveDate,
        revisionReason: revisionNotes,
      });
      setMsg({ type: 'success', text: `Salary revision saved! New CTC: ${rupees.format(revisedCtc)}` });
      setRevisionModal(false);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to record salary revision' });
    }
  }

  async function handleCalculateGratuity() {
    if (!employee) return;
    try {
      const res = await calculateGratuity({
        employeeId: employee.id,
      });
      setGratuityEstimate(res.data);
      setMsg({ type: 'success', text: 'Statutory gratuity calculated under active rule!' });
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to calculate gratuity' });
    }
  }

  if (error) {
    return (
      <div className="empty">
        <strong>This record is not available to you</strong>
        It may not exist, or it may sit outside what your role covers.{' '}
        <Link to="/employees">Back to the register</Link>
      </div>
    );
  }
  if (!employee) return <div className="empty">Loading employee 360 record…</div>;

  const isSelf = employee.id === session?.user.employeeId;
  const canSeePay = can('compensation.view') || (isSelf && can('compensation.view.self'));
  const canManageCareer = can('career.manage') || can('employee.edit');

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow mono">{employee.employee_code}</div>
          <h1>{employee.first_name} {employee.last_name}</h1>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {employee.designation || 'Staff'} • {employee.location || 'Headquarters'}
          </div>
        </div>
        <div className="toolbar">
          <StatusBadge status={employee.status} />
          <Link to="/employees" className="btn-quiet" style={{ textDecoration: 'none' }}>Back to Register</Link>
        </div>
      </div>

      {msg && (
        <div className={`alert ${msg.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          <Icon name={msg.type === 'success' ? 'check' : 'alertCircle'} />
          <span>{msg.text}</span>
          <button className="btn-quiet" style={{ marginLeft: 'auto' }} onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {/* 360 Lifecycle Navigation Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
          <Icon name="employees" /> Profile & Pay 360
        </button>
        <button className={`tab-btn ${tab === 'onboarding' ? 'active' : ''}`} onClick={() => setTab('onboarding')}>
          <Icon name="check" /> Onboarding & Probation {onboardingWf ? `(${onboardingWf.overall_progress_percent}%)` : ''}
        </button>
        <button className={`tab-btn ${tab === 'promotions' ? 'active' : ''}`} onClick={() => setTab('promotions')}>
          <Icon name="award" /> Career & Promotions ({promotions.length})
        </button>
        <button className={`tab-btn ${tab === 'transfers' ? 'active' : ''}`} onClick={() => setTab('transfers')}>
          <Icon name="refresh" /> Transfers & Location ({transfers.length})
        </button>
        <button className={`tab-btn ${tab === 'revisions' ? 'active' : ''}`} onClick={() => setTab('revisions')}>
          <Icon name="finance" /> Salary Revisions ({revisions.length})
        </button>
        <button className={`tab-btn ${tab === 'gratuity' ? 'active' : ''}`} onClick={() => setTab('gratuity')}>
          <Icon name="lock" /> Gratuity Simulation
        </button>
        <button className={`tab-btn ${tab === 'documents' ? 'active' : ''}`} onClick={() => setTab('documents')}>
          <Icon name="fileText" /> Documents & Letters ({employeeDocs.length})
        </button>
        <button className={`tab-btn ${tab === 'access' ? 'active' : ''}`} onClick={() => setTab('access')}>
          <Icon name="shieldCheck" /> Persona Roles & Access
        </button>
        <button className={`tab-btn ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>
          <Icon name="clock" /> Audit Trail ({history.length})
        </button>
      </div>

      {/* TAB: PERSONA ROLES & ACCESS CONTROL */}
      {tab === 'access' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" style={{ padding: '24px 28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14, marginBottom: 18 }}>
              <div>
                <span className="eyebrow">Identity & Access Governance</span>
                <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>
                  System Access & Persona Roles for {employee.first_name} {employee.last_name}
                </h2>
                <p className="muted" style={{ margin: '4px 0 0 0', fontSize: 12.5 }}>
                  Empower CEO and Administrators to grant or modify internal roles, permissions, and responsibilities for this employee.
                </p>
              </div>

              <Link to="/access-control" className="btn-quiet" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="shieldCheck" size="sm" />
                <span>Central IAM Governance</span>
              </Link>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 24 }}>
              <div style={{ padding: '14px 16px', background: 'var(--bg-field)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                <span className="muted" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Login User Account</span>
                <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>
                  {linkedUser ? linkedUser.email : (employee.work_email || employee.personal_email || 'No login account linked')}
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  Status: <strong style={{ color: linkedUser?.status === 'active' ? '#22c55e' : '#eab308' }}>{linkedUser ? linkedUser.status.toUpperCase() : 'NOT CREATED'}</strong>
                </div>
              </div>

              <div style={{ padding: '14px 16px', background: 'var(--bg-field)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                <span className="muted" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Persona Roles</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {userRoles.map((rk) => (
                    <span key={rk} className="badge" style={{ fontSize: 11, fontWeight: 600, background: 'var(--brand-light)', color: 'var(--brand-primary)' }}>
                      {rk.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ padding: '14px 16px', background: 'var(--bg-field)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
                <span className="muted" style={{ fontSize: 11, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last Authentication</span>
                <div className="mono" style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>
                  {linkedUser?.lastLoginAt ? new Date(linkedUser.lastLoginAt).toLocaleString() : 'Never logged in'}
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 20 }}>
              <h3 style={{ fontSize: 15, margin: '0 0 12px 0' }}>Assign Internal System Roles:</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginBottom: 20 }}>
                {[
                  { key: 'employee', label: 'Employee (Self-Service)', desc: 'Attendance clock-in, leave requests, payslips & personal claims' },
                  { key: 'manager', label: 'Team Lead / Manager', desc: 'Direct reports roster, leave & expense approvals, interview scorecards' },
                  { key: 'hr_admin', label: 'HR Administrator', desc: 'Directory management, recruitment ATS, templates, document vault & lifecycle' },
                  { key: 'payroll_admin', label: 'Payroll Admin', desc: 'Salary structures, payroll run execution, statutory filing & payslip generation' },
                  { key: 'finance_manager', label: 'Finance Manager', desc: 'General ledger, treasury bank accounts, AP/AR invoicing & cost budgets' },
                  { key: 'ceo_director', label: 'CEO / Director', desc: 'Executive intelligence, department budgets, company strategy & P&L' },
                  { key: 'organization_owner', label: 'Organisation Owner', desc: 'Global enterprise control, super admin governance & bulk ingestion' },
                  { key: 'auditor', label: 'Compliance Auditor', desc: 'Read-only financial & workforce compliance inspection with audit logs' },
                ].map((item) => {
                  const isChecked = userRoles.includes(item.key);
                  return (
                    <label
                      key={item.key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '12px 14px',
                        borderRadius: 8,
                        background: isChecked ? 'rgba(79, 70, 229, 0.08)' : 'var(--bg-field)',
                        border: isChecked ? '1px solid var(--brand-primary)' : '1px solid var(--border-subtle)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setUserRoles([...userRoles, item.key]);
                          } else {
                            setUserRoles(userRoles.filter((r) => r !== item.key));
                          }
                        }}
                        style={{ marginTop: 2 }}
                      />
                      <div style={{ flex: 1 }}>
                        <strong style={{ fontSize: 13, display: 'block', color: isChecked ? 'var(--brand-primary)' : 'inherit' }}>
                          {item.label}
                        </strong>
                        <span className="muted" style={{ fontSize: 11, lineHeight: 1.3, display: 'block', marginTop: 2 }}>
                          {item.desc}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button
                  className="btn"
                  onClick={handleSaveAccessRoles}
                  disabled={savingAccess}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <Icon name="shieldCheck" size="sm" />
                  <span>{savingAccess ? 'Applying Access...' : linkedUser ? 'Update Assigned Roles' : 'Grant Login Access & Assign Roles'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 1: OVERVIEW & COMPENSATION */}
      {tab === 'overview' && (
        <div className="detail-grid">
          <div className="stack">
            <div className="panel">
              <div className="panel-head"><h2>Employment Placement</h2></div>
              <dl>
                <Field label="Designation">{employee.designation ?? '—'}</Field>
                <Field label="Employment Type">{employee.employment_type}</Field>
                <Field label="Date of Joining"><span className="mono">{employee.date_of_joining}</span></Field>
                <Field label="Work Location">{employee.location ?? '—'}</Field>
                <Field label="Official Email">{employee.work_email ?? '—'}</Field>
                <Field label="Lifecycle Status"><StatusBadge status={employee.status} /></Field>
              </dl>
            </div>

            <div className="panel">
              <div className="panel-head"><h2>Personal Contact Information</h2></div>
              <dl>
                <Field label="Date of Birth">
                  {employee.date_of_birth !== undefined ? <span className="mono">{employee.date_of_birth ?? '—'}</span> : <Redacted permission="employee.view" />}
                </Field>
                <Field label="Personal Phone">
                  {employee.phone !== undefined ? (employee.phone ?? '—') : <Redacted permission="employee.view" />}
                </Field>
                <Field label="Personal Email">
                  {employee.personal_email !== undefined ? (employee.personal_email ?? '—') : <Redacted permission="employee.view" />}
                </Field>
              </dl>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h2>Current Compensation Structure</h2>
              {canSeePay && employee.compensation && (
                <span className="muted" style={{ fontSize: 12 }}>Effective {employee.compensation.effective_from}</span>
              )}
            </div>
            <dl>
              {!canSeePay ? (
                <Field label="Annual CTC"><Redacted permission="compensation.view" /></Field>
              ) : employee.compensation ? (
                <>
                  <Field label="Annual CTC">
                    <span className="money" style={{ fontSize: 18, color: '#22c55e' }}>{rupees.format(employee.compensation.ctc_annual)}</span>
                  </Field>
                  <Field label="Basic Salary (Monthly)">
                    <span className="money">{rupees.format(employee.compensation.basic_monthly)}</span>
                  </Field>
                  <Field label="House Rent Allowance (HRA)">
                    <span className="money">{rupees.format(employee.compensation.hra_monthly)}</span>
                  </Field>
                  <Field label="Other Special Allowances">
                    <span className="money">{rupees.format(employee.compensation.other_allowance_monthly)}</span>
                  </Field>
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <button className="btn-quiet" onClick={() => setRevisionModal(true)}>
                      <Icon name="plus" /> Propose Compensation Revision
                    </button>
                  </div>
                </>
              ) : (
                <Field label="Annual CTC"><span className="muted">No salary structure recorded.</span></Field>
              )}
            </dl>

            {/* Smart Contextual Document Recommendations */}
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                    Smart Official Documents & Letters
                  </h3>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    Quickly generate certified letters with frozen snapshot data
                  </span>
                </div>
                <button
                  className="btn-quiet"
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setSelectedTemplateId(templates[0]?.id || '');
                    setDocModal(true);
                  }}
                >
                  <Icon name="plus" /> All Templates
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {templates.slice(0, 4).map((tpl) => (
                  <div
                    key={tpl.id}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border-color)',
                      background: 'rgba(255,255,255,0.02)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 6,
                    }}
                  >
                    <div>
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#818cf8', textTransform: 'uppercase' }}>
                        {tpl.category}
                      </span>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', marginTop: 2 }}>
                        {tpl.name}
                      </div>
                    </div>
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: 11, padding: '4px 8px', width: '100%' }}
                      onClick={() => handleGenerateQuickDoc(tpl.id)}
                    >
                      Generate & Freeze
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: ONBOARDING & PROBATION */}
      {tab === 'onboarding' && (
        <div>
          {!onboardingWf ? (
            <div className="panel">
              <div className="empty">
                <strong>No Active Onboarding Workflow</strong>
                This employee record was created without an automated onboarding workflow.
              </div>
            </div>
          ) : (
            <div>
              {/* Progress Summary Card */}
              <div className="panel" style={{ marginBottom: 20 }}>
                <div className="panel-head">
                  <div>
                    <h2>{onboardingWf.template_name}</h2>
                    <span className="muted" style={{ fontSize: 13 }}>
                      Probation Period: {onboardingWf.probation_months} Months (Target End: {onboardingWf.probation_end_date || '—'})
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <StatusBadge status={onboardingWf.confirmation_status} />
                    {canManageCareer && onboardingWf.confirmation_status !== 'confirmed' && (
                      <button className="btn" onClick={() => setProbationModal(true)}>
                        <Icon name="check" /> Confirm Probation
                      </button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span>Overall Completion</span>
                    <strong>{onboardingWf.overall_progress_percent}%</strong>
                  </div>
                  <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                    <div
                      style={{
                        background: '#22c55e',
                        height: '100%',
                        width: `${onboardingWf.overall_progress_percent}%`,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Tasks List */}
              <div className="panel">
                <div className="panel-head">
                  <h2>Onboarding Checklist & Induction Tasks ({onboardingTasks.length})</h2>
                </div>
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>Status</th>
                      <th>Task Title</th>
                      <th>Stage Category</th>
                      <th>Assigned Role</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onboardingTasks.map((task: OnboardingTask) => (
                      <tr key={task.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={task.status === 'completed'}
                            onChange={() => handleToggleTask(task.id, task.status)}
                          />
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, textDecoration: task.status === 'completed' ? 'line-through' : 'none' }}>
                            {task.task_title}
                          </div>
                          <div className="muted" style={{ fontSize: 12 }}>{task.task_description}</div>
                        </td>
                        <td><span className="pill">{task.stage_name}</span></td>
                        <td>{task.assigned_role}</td>
                        <td>
                          <button
                            className="btn-quiet"
                            style={{ fontSize: 12, padding: '2px 8px' }}
                            onClick={() => handleToggleTask(task.id, task.status)}
                          >
                            {task.status === 'completed' ? 'Undo' : 'Mark Done'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 3: PROMOTIONS */}
      {tab === 'promotions' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Career Progression & Grade Promotions</h2>
            {canManageCareer && (
              <button className="btn" onClick={() => setPromoModal(true)}>
                <Icon name="plus" /> Request Promotion
              </button>
            )}
          </div>
          {promotions.length === 0 ? (
            <div className="empty">No career promotions logged for this employee.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Designation Transition</th>
                  <th>New Grade</th>
                  <th>CTC Adjustment</th>
                  <th>Justification</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((p) => (
                  <tr key={p.id}>
                    <td className="mono">{p.effective_date}</td>
                    <td>
                      <div><strong>{p.new_designation}</strong></div>
                      <div className="muted" style={{ fontSize: 12 }}>From: {p.previous_designation}</div>
                    </td>
                    <td><span className="pill">{p.new_grade || 'Standard'}</span></td>
                    <td>
                      {p.new_ctc ? (
                        <div style={{ fontWeight: 600, color: '#22c55e' }}>{rupees.format(p.new_ctc)}</div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ maxWidth: 220, fontSize: 13 }}>{p.justification}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td>
                      {p.status === 'submitted' && canManageCareer && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-quiet" onClick={() => handleReviewPromotion(p.id, 'approved')}>
                            Approve
                          </button>
                          <button className="btn-quiet" onClick={() => handleReviewPromotion(p.id, 'rejected')}>
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 4: TRANSFERS */}
      {tab === 'transfers' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Departmental & Location Transfers</h2>
            {canManageCareer && (
              <button className="btn" onClick={() => setTransferModal(true)}>
                <Icon name="plus" /> Execute Transfer
              </button>
            )}
          </div>
          {transfers.length === 0 ? (
            <div className="empty">No transfer records found for this employee.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Department</th>
                  <th>Location</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((t) => (
                  <tr key={t.id}>
                    <td className="mono">{t.effective_date}</td>
                    <td>
                      <div><strong>{t.to_department_name || '—'}</strong></div>
                      <div className="muted" style={{ fontSize: 12 }}>From: {t.from_department_name || '—'}</div>
                    </td>
                    <td>
                      <div><strong>{t.to_location || '—'}</strong></div>
                      <div className="muted" style={{ fontSize: 12 }}>From: {t.from_location || '—'}</div>
                    </td>
                    <td>{t.reason || '—'}</td>
                    <td><StatusBadge status={t.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 5: SALARY REVISIONS */}
      {tab === 'revisions' && (
        <div className="panel">
          <div className="panel-head">
            <h2>Compensation Revision History</h2>
            {canSeePay && (
              <button className="btn" onClick={() => setRevisionModal(true)}>
                <Icon name="plus" /> New Salary Revision
              </button>
            )}
          </div>
          {revisions.length === 0 ? (
            <div className="empty">No historical salary revisions logged.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Revision Type</th>
                  <th>Previous CTC</th>
                  <th>New Annual CTC</th>
                  <th>Increment %</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {revisions.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.effective_from}</td>
                    <td style={{ textTransform: 'capitalize' }}><span className="pill">{r.revision_type.replace('_', ' ')}</span></td>
                    <td className="mono">{rupees.format(r.previous_ctc_annual)}</td>
                    <td className="mono" style={{ fontWeight: 600, color: '#22c55e' }}>{rupees.format(r.new_ctc_annual)}</td>
                    <td>
                      <span className="pill" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#4ade80' }}>
                        +{r.increment_percentage}%
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{r.revision_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 6: GRATUITY SIMULATION */}
      {tab === 'gratuity' && (
        <div className="detail-grid">
          <div className="panel">
            <div className="panel-head">
              <h2>Continuous Service & Gratuity Calculator</h2>
            </div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
              Calculates statutory gratuity according to the Payment of Gratuity Act formula: <br />
              <code style={{ color: '#93c5fd' }}>(15 × Last Drawn Basic Salary × Qualifying Service Years) / 26</code>
            </p>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <button className="btn" onClick={handleCalculateGratuity}>
                Run Statutory Calculation &rarr;
              </button>
            </div>

            {gratuityEstimate ? (
              <dl>
                <Field label="Date of Joining"><span className="mono">{gratuityEstimate.date_of_joining}</span></Field>
                <Field label="Total Continuous Service">{gratuityEstimate.total_service_years} Years</Field>
                <Field label="Qualifying Service Years">
                  <strong>{gratuityEstimate.qualifying_service_years} Years</strong>
                  <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>(Rounded by &gt;= 6 months rule)</span>
                </Field>
                <Field label="Last Drawn Basic">{rupees.format(gratuityEstimate.last_drawn_basic)}</Field>
                <Field label="Estimated Gratuity Payable">
                  <span className="money" style={{ fontSize: 20, color: '#22c55e' }}>
                    {rupees.format(gratuityEstimate.estimated_gratuity_amount)}
                  </span>
                </Field>
                <Field label="Statutory Ceiling Applied">₹20,00,000 statutory cap check verified</Field>
                <Field label="Advisory Determination">
                  <span className="muted" style={{ fontSize: 12 }}>{gratuityEstimate.legal_determination_notes}</span>
                </Field>
              </dl>
            ) : (
              <div className="empty">Click "Run Statutory Calculation" to evaluate tenure and estimated gratuity.</div>
            )}
          </div>

          <div className="panel">
            <div className="panel-head"><h2>Statutory Rules & Eligibility</h2></div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: '#cbd5e1' }}>
              <p><strong>Minimum Qualifying Service:</strong> 5 Years of continuous service (exempted in cases of death/disability).</p>
              <p><strong>Factor Basis:</strong> 15 working days per year of service based on a 26-day statutory month.</p>
              <p><strong>Separation Payout:</strong> Incorporated automatically into the Full & Final (F&F) settlement ledger upon resignation approval.</p>
            </div>
          </div>
        </div>
      )}

      {/* TAB: DOCUMENTS & LETTERS VAULT */}
      {tab === 'documents' && (
        <div className="panel">
          <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2>Employee Document Vault & Certified Records</h2>
              <span className="muted" style={{ fontSize: 13 }}>
                Frozen immutable documents, appointment letters, salary certificates and increment notices
              </span>
            </div>
            <button
              className="btn"
              onClick={() => {
                setSelectedTemplateId(templates[0]?.id || '');
                setDocModal(true);
              }}
            >
              <Icon name="plus" /> Generate New Letter
            </button>
          </div>

          {employeeDocs.length === 0 ? (
            <div className="empty" style={{ padding: 40, textAlign: 'center' }}>
              <Icon name="fileText" size={40} />
              <div style={{ marginTop: 12, fontWeight: 600 }}>No Official Documents Issued Yet</div>
              <p className="muted" style={{ fontSize: 13 }}>
                Generate official offer letters, appointment agreements, or salary certificates using master templates.
              </p>
              <button
                className="btn"
                style={{ marginTop: 16 }}
                onClick={() => {
                  setSelectedTemplateId(templates[0]?.id || '');
                  setDocModal(true);
                }}
              >
                Generate First Document
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', marginTop: 16 }}>
              <table className="table" style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '10px 12px' }}>Document Ref</th>
                    <th style={{ padding: '10px 12px' }}>Title & Category</th>
                    <th style={{ padding: '10px 12px' }}>Version</th>
                    <th style={{ padding: '10px 12px' }}>Status</th>
                    <th style={{ padding: '10px 12px' }}>Issued / Created</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeDocs.map((doc) => (
                    <tr key={doc.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 600 }}>
                        {doc.document_number}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: 600 }}>{doc.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                          {doc.category}
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: 11 }}>
                        {doc.template_version}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: 4,
                            background:
                              doc.status === 'issued' || doc.status === 'generated'
                                ? 'rgba(16,185,129,0.15)'
                                : doc.status === 'approved'
                                ? 'rgba(99,102,241,0.15)'
                                : 'rgba(245,158,11,0.15)',
                            color:
                              doc.status === 'issued' || doc.status === 'generated'
                                ? '#34d399'
                                : doc.status === 'approved'
                                ? '#818cf8'
                                : '#fbbf24',
                          }}
                        >
                          {doc.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: 'var(--text-secondary)' }}>
                        {new Date(doc.created_at).toLocaleDateString('en-IN')}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            className="btn-quiet"
                            style={{ fontSize: 12 }}
                            onClick={() => setPreviewDoc(doc)}
                          >
                            Preview
                          </button>
                          <button
                            className="btn-quiet"
                            style={{ fontSize: 12, color: '#38bdf8' }}
                            onClick={() => handleDownloadEmployeeDoc(doc.id)}
                            title="Download Official Record"
                          >
                            <Icon name="download" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TAB 7: AUDIT TRAIL */}
      {tab === 'history' && (
        <div className="panel">
          <div className="panel-head"><h2>Immutable Lifecycle Audit Log</h2></div>
          {!can('audit.view') ? (
            <div style={{ padding: 16 }}><Redacted permission="audit.view" /></div>
          ) : history.length === 0 ? (
            <div className="empty">No audit entries recorded for this employee.</div>
          ) : (
            history.map((entry) => (
              <div className="audit-entry" key={entry.id}>
                <div className="audit-when">{new Date(entry.created_at).toLocaleString('en-IN')}</div>
                <div className="audit-action">{entry.action}</div>
                {entry.reason && <div className="muted">{entry.reason}</div>}
              </div>
            ))
          )}
        </div>
      )}

      {/* MODAL 1: CONFIRM PROBATION */}
      {probationModal && onboardingWf && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Confirm / Review Probation</h2>
              <button className="btn-quiet" onClick={() => setProbationModal(false)}>✕</button>
            </div>
            <form onSubmit={handleConfirmProbation}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Confirmation Decision</label>
                <select
                  className="input"
                  value={probationStatus}
                  onChange={(e: any) => setProbationStatus(e.target.value)}
                >
                  <option value="confirmed">Confirm Permanent Employment</option>
                  <option value="extended">Extend Probationary Period</option>
                  <option value="terminated">Terminate during Probation</option>
                </select>
              </div>

              {probationStatus === 'extended' && (
                <div style={{ marginBottom: 12 }}>
                  <label className="label">Extension Duration (Months)</label>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={12}
                    value={extendMonths}
                    onChange={(e) => setExtendMonths(Number(e.target.value))}
                  />
                </div>
              )}

              <div style={{ marginBottom: 16 }}>
                <label className="label">Evaluation & Performance Notes</label>
                <textarea
                  className="input"
                  rows={3}
                  value={probationNotes}
                  onChange={(e) => setProbationNotes(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setProbationModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ background: '#22c55e' }}>Confirm Decision</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: REQUEST PROMOTION */}
      {promoModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>Submit Promotion Requisition</h2>
              <button className="btn-quiet" onClick={() => setPromoModal(false)}>✕</button>
            </div>
            <form onSubmit={handleRequestPromotion}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Proposed Designation</label>
                  <input
                    className="input"
                    value={newDesignation}
                    onChange={(e) => setNewDesignation(e.target.value)}
                    required
                    placeholder="e.g. Lead Engineer"
                  />
                </div>
                <div>
                  <label className="label">Proposed Grade</label>
                  <input
                    className="input"
                    value={newGrade}
                    onChange={(e) => setNewGrade(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Proposed Annual CTC (₹)</label>
                  <input
                    className="input"
                    type="number"
                    value={newCtc}
                    onChange={(e) => setNewCtc(Number(e.target.value))}
                    required
                  />
                </div>
                <div>
                  <label className="label">Effective Date</label>
                  <input
                    className="input"
                    type="date"
                    value={promoEffectiveDate}
                    onChange={(e) => setPromoEffectiveDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Promotion Justification</label>
                <textarea
                  className="input"
                  rows={3}
                  value={promoJustification}
                  onChange={(e) => setPromoJustification(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setPromoModal(false)}>Cancel</button>
                <button type="submit" className="btn">Submit for Approval</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: EXECUTE TRANSFER */}
      {transferModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 540 }}>
            <div className="modal-head">
              <h2>Execute Transfer / Relocation</h2>
              <button className="btn-quiet" onClick={() => setTransferModal(false)}>✕</button>
            </div>
            <form onSubmit={handleExecuteTransfer}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">Destination Department</label>
                  <select
                    className="input"
                    value={transferDeptId}
                    onChange={(e) => setTransferDeptId(e.target.value)}
                  >
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Destination Location</label>
                  <input
                    className="input"
                    value={transferLocation}
                    onChange={(e) => setTransferLocation(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">New Reporting Manager</label>
                  <select
                    className="input"
                    value={transferManagerId}
                    onChange={(e) => setTransferManagerId(e.target.value)}
                  >
                    <option value="">No Manager Change</option>
                    {allEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.designation}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Effective Date</label>
                  <input
                    className="input"
                    type="date"
                    value={transferEffectiveDate}
                    onChange={(e) => setTransferEffectiveDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Transfer Business Reason</label>
                <textarea
                  className="input"
                  rows={2}
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setTransferModal(false)}>Cancel</button>
                <button type="submit" className="btn">Execute Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: SALARY REVISION */}
      {revisionModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 500 }}>
            <div className="modal-head">
              <h2>Record Salary Revision</h2>
              <button className="btn-quiet" onClick={() => setRevisionModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateRevision}>
              <div style={{ marginBottom: 12 }}>
                <label className="label">Revision Type</label>
                <select
                  className="input"
                  value={revisionType}
                  onChange={(e: any) => setRevisionType(e.target.value)}
                >
                  <option value="appraisal">Annual / Merit Appraisal</option>
                  <option value="promotion">Promotion Adjustment</option>
                  <option value="market_correction">Market Benchmark Correction</option>
                  <option value="retention">Retention Revision</option>
                </select>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label className="label">New Annual CTC (₹)</label>
                  <input
                    className="input"
                    type="number"
                    value={revisedCtc}
                    onChange={(e) => setRevisedCtc(Number(e.target.value))}
                    required
                  />
                </div>
                <div>
                  <label className="label">Effective Date</label>
                  <input
                    className="input"
                    type="date"
                    value={revisionEffectiveDate}
                    onChange={(e) => setRevisionEffectiveDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Revision Rationale Notes</label>
                <textarea
                  className="input"
                  rows={2}
                  value={revisionNotes}
                  onChange={(e) => setRevisionNotes(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setRevisionModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ background: '#22c55e' }}>Confirm Revision</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 5: QUICK DOCUMENT GENERATION */}
      {docModal && (
        <div className="modal-backdrop">
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-head">
              <h2>Generate Official Document</h2>
              <button className="btn-quiet" onClick={() => setDocModal(false)}>✕</button>
            </div>
            <div>
              <div style={{ marginBottom: 14 }}>
                <label className="label">Select Master Document Template</label>
                <select
                  className="input"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      [{tpl.category.toUpperCase()}] {tpl.name} ({tpl.version})
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label className="label">Custom Title (Optional)</label>
                <input
                  className="input"
                  type="text"
                  value={docTitleOverride}
                  onChange={(e) => setDocTitleOverride(e.target.value)}
                  placeholder="e.g. Annual Merit Salary Certificate"
                />
              </div>

              <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 18 }}>
                The document will freeze an immutable snapshot of <strong>{employee?.first_name} {employee?.last_name}</strong>'s current compensation, designation, joining date and organizational placement.
              </p>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setDocModal(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => handleGenerateQuickDoc()}
                >
                  Generate & Freeze Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 6: PREVIEW DOCUMENT */}
      {previewDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="card" style={{ maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: '#0b0f19' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
                  {previewDoc.title} ({previewDoc.document_number})
                </h3>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Status: <strong>{previewDoc.status}</strong> • Version: {previewDoc.template_version}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleDownloadEmployeeDoc(previewDoc.id)}
                  className="btn btn-primary"
                  style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Icon name="download" size={14} /> Download HTML / Print
                </button>
                <button
                  onClick={() => setPreviewDoc(null)}
                  className="btn btn-secondary"
                  style={{ padding: '6px 10px', fontSize: '12px' }}
                >
                  Close
                </button>
              </div>
            </div>

            <div style={{ flex: 1, padding: '16px', background: '#fff', overflowY: 'auto' }}>
              <iframe
                title="Document View"
                srcDoc={previewDoc.rendered_content_html}
                style={{ width: '100%', height: '600px', border: 'none', background: '#fff' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
